/* eslint-disable max-len */
import { VoidCallback } from '@deqo/foundation';
import React, { useCallback, useMemo, useState } from 'react';
import { useObserver } from '../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../Legacy';
import { WorkspaceLiteData } from '../../api/ApiTypes';
import { CommandDispatcher } from '../../concepts/commands/CommandDispatcher';
import { Icon, NoIcon } from '../../concepts/resources/Icon';
import { useResolve } from '../Container';
import { ProfileModel } from '../ProfileModel';
import { AccountHeaderBlock } from '../account/AccountHeaderBlock';
import { PlanStateHeaderBlock } from '../account/PlanStateHeaderBlock';
import { Crumbs } from '../common/Crumbs';
import { EnableIfHasFeature, HideIfHasFeature, useFeatureCheck } from '../common/FeatureElements';
import { Page } from '../common/Page';
import { usePermissionCheck } from '../common/PermissionElements';
import { WorkspaceModel } from '../dashboard/Models';
import { Flexer, Title } from '../dashboard/elements/LayoutElements';
import { XmasLightsDashboard } from '../occasions/xmas/XmasLightsDashboard';
import { Box, Button, Colors, Menu } from './../../ui';
import { boardRestore } from './BoardCommands';
import { FolderBoardView } from './FolderBoardView';
import {
    workspaceImportLegacy,
    workspaceInviteViaPopup,
    workspaceJoin,
    workspaceLeave,
    workspaceRename,
    workspaceSetPublic
} from './WorkspaceCommands';
import { WorkspaceFeatureCallout } from './WorkspaceFeatureCallout';
import { WorkspacePageContent } from './WorkspacePageContent';
import { WorkspaceStripMenu } from './WorkspaceStripMenu';

const WorkspaceMenu: LFC<{ workspace: WorkspaceLiteData, onEdit: VoidCallback }> = ({ workspace, onEdit }) => {

    const dispatch = useResolve(CommandDispatcher);
    const profile = useObserver(useResolve(ProfileModel));
    const { id, public: pub } = useObserver(useResolve(WorkspaceModel));

    const [open, setOpen] = useState(false);
    const close = () => {
        setOpen(false);
        return true;
    };

    const onLeave = useCallback(() => close() && dispatch(workspaceLeave, [id]), [id]);
    const onInvite = useCallback(() => close() && dispatch(workspaceInviteViaPopup, [id]), [id]);
    const onMakePublic = useCallback(() => close() && dispatch(workspaceSetPublic, [id, true]), [id]);
    const onMakePrivate = useCallback(() => close() && dispatch(workspaceSetPublic, [id, false]), [id]);
    const onImportLegacy = useCallback(() => close() && dispatch(workspaceImportLegacy), []);
    const onRestoreBoard = useCallback(() => close() && dispatch(boardRestore), []);

    return <Crumbs.Menu label={workspace.label} open={open} setOpen={setOpen}>
        <Box minWidth="12">
            <Menu>
                {workspace.type === 'team' && <>
                    <Menu.Item onClick={onEdit}>
                        <Icon size="1.2" src="deqo://icons/hero/pencil-alt" />
                        <Menu.ItemText>
                            Rename workspace
                        </Menu.ItemText>
                    </Menu.Item>
                    <Menu.Item onClick={onLeave}>
                        <Icon size="1.2" src="deqo://icons/hero/logout" />
                        <Menu.ItemText>
                            Leave workspace
                        </Menu.ItemText>
                    </Menu.Item>
                    <Menu.Divide />
                </>}
                {(workspace.type === 'user' || workspace.type === 'team') && <>
                    <Menu.Item onClick={onImportLegacy}>
                        <NoIcon size="1.2" />
                        <Menu.ItemText>
                            Import Legacy Boards
                        </Menu.ItemText>
                    </Menu.Item>
                    {/* {profile.flags.super && <Menu.Item onClick={onRestoreBoard}><NoIcon size="1.2" />Restore Backup</Menu.Item>} */}
                    {workspace.type === 'team' && <Menu.Divide />}
                </>}
                {workspace.type === 'team' && <>
                    <Menu.Item onClick={onInvite}>
                        <Icon size="1.2" src="deqo://icons/hero/user-add" />
                        <Menu.ItemText>
                            Invite people
                        </Menu.ItemText>
                    </Menu.Item>
                    {pub && <Menu.Item onClick={onMakePrivate}>
                        <Icon size="1.2" src="deqo://icons/hero/lock-closed" />
                        <Menu.ItemText>
                            Make private
                        </Menu.ItemText>
                    </Menu.Item>}
                    {!pub && <Menu.Item onClick={onMakePublic}>
                        <Icon size="1.2" src="deqo://icons/hero/lock-open" />
                        <Menu.ItemText>
                            Make public
                        </Menu.ItemText>
                    </Menu.Item>}
                </>}
            </Menu>
        </Box>
    </Crumbs.Menu>;
};

const WorkspaceJoinButton: LFC<{ workspace: WorkspaceLiteData }> = ({ workspace }) => {

    const dispatch = useResolve(CommandDispatcher);
    const onJoin = useCallback(() => dispatch(workspaceJoin, [workspace.id]), [workspace]);

    return <Box padding="0 0 0 1" >
        <Button size="xs" variant="positive" onAction={onJoin}>Join Workspace</Button>
    </Box>;
};

const WorkspaceCrumbs = () => {

    const dispatch = useResolve(CommandDispatcher);
    const workspace = useObserver(useResolve(WorkspaceModel));
    const [editing, setEditing] = useState(false);
    const featureEnabled = useFeatureCheck('workspaces');
    const canEdit = featureEnabled && usePermissionCheck('da:write', 'wo:write') && workspace.type !== 'library' && workspace.joined;

    const onApply = (value: string) => {
        setEditing(false);
        dispatch(workspaceRename, [workspace.id, value]);
    };

    const onCancel = () => setEditing(false);

    return <Crumbs>
        {!editing && canEdit &&
            <WorkspaceMenu workspace={workspace} onEdit={() => setEditing(true)} />
        }
        {!editing && !canEdit && <>
            <Crumbs.Title>{workspace.label}</Crumbs.Title>
        </>}
        {editing &&
            <Crumbs.Input initialValue={workspace.label} onApply={onApply} onCancel={onCancel} />
        }
        {!editing && !workspace.joined && featureEnabled && <>
            <WorkspaceJoinButton workspace={workspace} />
        </>}
    </Crumbs>;
};

const WorkspaceEmpty = () => {
    return <Flexer inset flex center pointerOff gap=".75">
        <Title color={Colors.gray400} flush size="3xl">There is nothing here yet!</Title>
        <Title color={Colors.gray400} size="lg">Create a board or folder with the buttons above.</Title>
    </Flexer>;
};

export const WorkspaceRootView = () => {

    const workspace = useResolve(WorkspaceModel);
    if (!workspace) return null;

    const { joined, boards, folders, type } = useObserver(workspace);

    const count = boards.length + folders.length;
    const ConfiguredBoardCreateStripMenu = useMemo(() => () => <WorkspaceStripMenu templates={type !== 'library'} />, [type]);

    return <>
        <Page.Header>
            <WorkspaceCrumbs />
            <Page.HeaderPush>
                <PlanStateHeaderBlock />
                <AccountHeaderBlock />
            </Page.HeaderPush>
        </Page.Header>
        <EnableIfHasFeature name="workspaces" flex grow>
            <WorkspacePageContent top={ConfiguredBoardCreateStripMenu} showSidebar={type !== 'user'}>
                <XmasLightsDashboard />
                {!!count && <FolderBoardView {...{ actions: joined, boards, folders }} />}
                {!count && <WorkspaceEmpty />}
            </WorkspacePageContent>
        </EnableIfHasFeature>
        <HideIfHasFeature name="workspaces">
            <WorkspaceFeatureCallout />
        </HideIfHasFeature>
    </>;
};

{/* <Observer store={useResolve(WorkspaceModel)} render={({ joined, boards, folders }) => */ }
